import { TILE } from '../config/tuning'
import type { ArmamentId, Entity } from '../ecs/entity'
import type { World } from '../ecs/world'
import { makeCorpse, makePickup, makeProjectile } from '../entities/templates'
import { PALETTE } from '../render/palette'

/**
 * Central damage/kill path so scoring, events, feedback, and corpse rules
 * stay consistent no matter what dealt the hit.
 */
export function damageEntity(world: World, e: Entity, amount: number, by: 'shot' | 'magic' | 'trap' | 'boss'): void {
  if (e.dead || e.hp === undefined) return
  if (e.etype === 'auditor') return // unkillable by normal means (magic uses removeAuditor)
  if (e.invuln && e.invuln > 0) return
  e.hp -= amount
  e.flash = world.tuning.feel.hitFlashTime
  // getting shot is loud: wake the target and its nearby pack, so camping a
  // doorway (or sniping the assembler) draws the room instead of free kills
  if (e.kind === 'enemy' || e.kind === 'generator') {
    e.awake = true
    const r2 = world.tuning.enemies.wake.packRadius ** 2
    for (const o of world.entities) {
      if (o.kind === 'enemy' && !o.dead && (o.x - e.x) ** 2 + (o.y - e.y) ** 2 < r2) o.awake = true
    }
  }
  if (e.hp > 0) {
    // non-lethal hit feedback (audio hangs off this, throttled on its side)
    if (e.kind === 'enemy' || e.kind === 'generator') world.bus.emit('enemy_hit', {})
    return
  }
  // bosses die through the framework (bossSystem -> dieBoss), which opens
  // the exit, dismisses adds, and emits boss_kill — never through killEntity
  if (e.kind === 'boss') return
  killEntity(world, e, by)
}

export function killEntity(world: World, e: Entity, by: 'shot' | 'magic' | 'trap' | 'boss'): void {
  if (e.dead) return
  e.dead = true
  if (e.kind === 'enemy' && e.etype && e.etype !== 'auditor') {
    // RUPTURE CLAUSE: destroyed hostiles burst into brief damaging shrapnel
    // (skip magic kills — a screen clear spawning 40 bursts is noise, not feel)
    if (world.amenities.has('rupture') && by !== 'magic') {
      const r = world.tuning.amenities.rupture
      const base = world.levelRng.range(0, Math.PI * 2)
      for (let i = 0; i < r.pellets; i++) {
        const a = base + (Math.PI * 2 * i) / r.pellets
        const frag = makeProjectile(world.tuning, e.x, e.y, Math.cos(a), Math.sin(a), r.speed, r.damage, 'participant')
        frag.lifetime = r.lifetime
        world.add(frag)
      }
    }
    const cfg = world.tuning.enemies[e.etype]
    world.addScore(cfg.score)
    world.bus.emit('enemy_killed', { type: e.etype, by: by === 'shot' || by === 'magic' ? by : 'trap' })
    world.burst(e.x, e.y, PALETTE.enemyBurst[e.etype] ?? PALETTE.hostile, world.tuning.feel.particleCount)
    // leave a corpse the Foreman can resurrect (not foremen themselves)
    if (e.etype !== 'foreman' && by !== 'magic') world.add(makeCorpse(e.etype, e.x, e.y))
    // boss-spawned adds sometimes drop rations so the arena fight stays survivable
    const parent = e.parentGen !== undefined ? world.entities.find((b) => b.id === e.parentGen && !b.dead) : undefined
    if (parent?.kind === 'boss' && world.levelRng.chance(world.tuning.boss.addFoodDropChance)) {
      world.add(makePickup('food', Math.floor(e.x / TILE), Math.floor(e.y / TILE)))
    }
  } else if (e.kind === 'generator' && e.gen) {
    world.addScore(world.tuning.generators.score)
    world.burst(e.x, e.y, PALETTE.generatorCore, world.tuning.feel.particleCount * 2, 180, 0.7, 4)
    world.addShake(world.tuning.feel.shakeSmall)
    // a dead assembler yields its stores — destroying them buys time against attrition
    for (let i = 0; i < world.tuning.generators.foodDrop; i++) {
      world.add(makePickup('food', Math.floor(e.x / TILE), Math.floor(e.y / TILE)))
    }
    // occasionally it yields the armament it was issued (level RNG stream)
    if (world.levelRng.chance(world.tuning.armaments.dropChance)) {
      const ids: ArmamentId[] = ['scatter', 'lance', 'seam', 'suppressor']
      world.add(makePickup('armament', Math.floor(e.x / TILE), Math.floor(e.y / TILE), ids[world.levelRng.int(0, ids.length - 1)]))
    }
    world.bus.emit('generator_destroyed', {
      genType: e.gen.genType,
      remaining: world.generators().filter((g) => g.id !== e.id).length
    })
  }
}

/** Damage the player through one choke point (invuln, feedback, death events). */
export function hurtPlayer(world: World, p: Entity, amount: number, cause: string): void {
  if (p.dead || p.hp === undefined || (p.invuln ?? 0) > 0) return
  // SURPLUS METABOLISM: the shield buffer absorbs before vitals
  if (p.shield && p.shield > 0) {
    const absorbed = Math.min(p.shield, amount)
    p.shield -= absorbed
    amount -= absorbed
    if (amount <= 0) {
      p.flash = world.tuning.feel.hitFlashTime
      p.invuln = world.tuning.player.invulnAfterHit
      return
    }
  }
  p.hp -= amount
  p.flash = world.tuning.feel.hitFlashTime
  p.invuln = world.tuning.player.invulnAfterHit
  world.addShake(world.tuning.feel.shakeSmall)
  world.bus.emit('player_hurt', { hp: Math.max(0, p.hp), by: cause })
  if (p.hp <= 0) {
    p.dead = true
    world.burst(p.x, p.y, PALETTE.participant, world.tuning.feel.particleCount * 2, 160, 0.8)
    world.addShake(world.tuning.feel.shakeBig)
    const payload = { cause, floor: world.floor }
    if (world.floor <= 1 && world.levelTime < 45) world.bus.emit('player_death_cheap', payload)
    else world.bus.emit('player_death', payload)
  }
}
